import { VueQueryPlugin, useQueryClient } from "@tanstack/vue-query";

import { setApi, setPublicApi } from "./src/api";
import getUserDevices from "./src/getUserDevices";
import getAccountUser from "./src/getAccountUser";
import getAccountGroups from "./src/getAccountGroups";
import getAccountShipments from "./src/getAccountShipments";
import getAccountShipmentMetrics from "./src/getAccountShipmentMetrics";
import getAccountDevices from "./src/getAccountDevices";
import getAccountDeviceList, {
    ACCOUNT_DEVICE_LIST_PER_PAGE,
    fetchAccountDeviceListPage,
} from "./src/getAccountDeviceList";
import { toApiPagination } from "./src/paginated-list-fetch";
import getAccountDevice from "./src/getAccountDevice";
import getAccountEvents from "./src/getAccountEvents";
import getAccountGeofences from "./src/getAccountGeofences";
import getAccountGeofence from "./src/getAccountGeofence";
import postDialog from "./src/mutations/postDialog";
import getModels from "./src/getModels";
import getModel from "./src/getModel";
import createModel from "./src/mutations/createModel";
import updateModel from "./src/mutations/updateModel";
import getCommandsByModel from "./src/getCommandsByModel";
import postAiDialogMessage from "./src/mutations/postAiDialogMessage";
import getAiDialogMessages from "./src/getAiDialogMessages";
import postAccountDialog from "./src/mutations/postAccountDialog";
import updateAiDialog from "./src/mutations/updateAiDialog";
import deleteAiDialog from "./src/mutations/deleteAiDialog";
import getAccountDialogs from "./src/getAccountDialogs";
import postMessageFeedback from "./src/mutations/postMessageFeedback";
import deleteMessageFeedback from "./src/mutations/deleteMessageFeedback";
import getCommandBroadcasts from "./src/getCommandBroadcasts";
import downloadCommandBroadcast from "./src/mutations/downloadCommandBroadcast";
import getAccountContext from "./src/getAccountContext";
import postAccountContext from "./src/mutations/postAccountContext";
import updateAccountContext from "./src/mutations/updateAccountContext";
import deleteAccountContext from "./src/mutations/deleteAccountContext";
import createAccountGeofence from "./src/mutations/createAccountGeofence";
import updateAccountGeofence from "./src/mutations/updateAccountGeofence";
import deleteAccountGeofence from "./src/mutations/deleteAccountGeofence";
import getInsights from "./src/getInsights";
import getPayments from "./src/getPayments";
import getPayment from "./src/getPayment";
import getDevicePurchaseReviews from "./src/getDevicePurchaseReviews";
import getDevicePurchaseReview from "./src/getDevicePurchaseReview";
import updateDevicePurchaseReviewSlots from "./src/mutations/updateDevicePurchaseReviewSlots";
import findPayment from "./src/findPayment";
import getInsight from "./src/getInsight";
import getAccountAiReports from "./src/getAccountAiReports";
import getAccountPlaceSuggestions from "./src/getAccountPlaceSuggestions";
import getAccountPlaceSuggestion from "./src/getAccountPlaceSuggestion";
import declineAccountPlaceSuggestion from "./src/mutations/declineAccountPlaceSuggestion";
import getAccountGeofenceRemovalSuggestions from "./src/getAccountGeofenceRemovalSuggestions";
import getAccountGeofenceRemovalSuggestion from "./src/getAccountGeofenceRemovalSuggestion";
import declineAccountGeofenceRemovalSuggestion from "./src/mutations/declineAccountGeofenceRemovalSuggestion";
import getAiReport from "./src/getAiReport";
import approveAiReport from "./src/mutations/approveAiReport";
import generateAiReport from "./src/mutations/generateAiReport";
import executeCommand from "./src/mutations/executeCommand";
import getAiCommand from "./src/getAiCommand";
import revertCommand from "./src/mutations/revertCommand";
import getBlacklistedMac from "./src/getBlacklistedMac";
import getFixedMac from "./src/getFixedMac";
import postFixedMac from "./src/mutations/postFixedMac";
import deleteFixedMac from "./src/mutations/deleteFixedMac";
import postBlacklistedMac from "./src/mutations/postBlacklistedMac";
import deleteBlacklistedMac from "./src/mutations/deleteBlacklistedMac";
import getAccount from "./src/getAccount";
import getPublicTracker from "./src/getPublicTracker";
import updateAccount from "./src/mutations/updateAccount";
import updatePayment from "./src/mutations/updatePayment";
import updateAddress from "./src/mutations/updateAddress";
import getPaymentParts from "./src/getPaymentParts";
import createPaymentPart from "./src/mutations/createPaymentPart";
import sendPaymentToQuickBooks from "./src/mutations/sendPaymentToQuickBooks";
import deletePaymentPart from "./src/mutations/deletePaymentPart";
import getPasskeyCredentials from "./src/getPasskeyCredentials";
import passkeyBeginLogin from "./src/mutations/passkeyBeginLogin";
import passkeyFinishLogin from "./src/mutations/passkeyFinishLogin";
import passkeyBeginRegister from "./src/mutations/passkeyBeginRegister";
import passkeyFinishRegister from "./src/mutations/passkeyFinishRegister";
import passkeyDelete from "./src/mutations/passkeyDelete";
import getAccountMapOverlays from "./src/getAccountMapOverlays";
import deleteMapOverlay from "./src/mutations/deleteMapOverlay";
import createMapOverlay from "./src/mutations/createMapOverlay";
import updateMapOverlay from "./src/mutations/updateMapOverlay";
import updateDevice from "./src/mutations/updateDevice";
import createAccountShipment from "./src/mutations/createAccountShipment";
import postAccountShipmentRouteSegment from "./src/mutations/postAccountShipmentRouteSegment";
import patchAccountShipmentRouteSegment from "./src/mutations/patchAccountShipmentRouteSegment";
import deleteAccountShipmentRouteSegment from "./src/mutations/deleteAccountShipmentRouteSegment";
import usePostAccountShipmentDevices from "./src/mutations/postAccountShipmentDevices";
import usePostAccountShipmentPublish from "./src/mutations/postAccountShipmentPublish";
import usePostAccountShipmentStops from "./src/mutations/postAccountShipmentStops";
import usePutAccountShipmentStops from "./src/mutations/putAccountShipmentStops";
import getAccountShipmentRoutesBatch from "./src/getAccountShipmentRoutesBatch";
import getAccountLearnedRoutes from "./src/getAccountLearnedRoutes";
import getAccountShipmentV2 from "./src/getAccountShipmentV2";
import { ACCOUNT_SHIPMENT_V2_INCLUDE } from "./src/getAccountShipmentV2";
import usePatchAccountShipmentStop from "./src/mutations/patchAccountShipmentStop";
import usePatchAccountShipmentV2 from "./src/mutations/patchAccountShipmentV2";
import usePostAccountShipmentFinished from "./src/mutations/postAccountShipmentFinished";
import usePostAccountShipmentsBulkArchive from "./src/mutations/postAccountShipmentsBulkArchive";
import usePostAccountShipmentsBulkDelete from "./src/mutations/postAccountShipmentsBulkDelete";
import getAccountShipmentEvents from "./src/getAccountShipmentEvents";
import getAccountExceptions from "./src/getAccountExceptions";
import getAccountExceptionMetrics from "./src/getAccountExceptionMetrics";
import getAccountShipmentExceptions from "./src/getAccountShipmentExceptions";
import usePostAccountExceptionClosed from "./src/mutations/postAccountExceptionClosed";
import useDeleteAccountExceptionClosed from "./src/mutations/deleteAccountExceptionClosed";
import usePostAccountExceptionsBulkClosed from "./src/mutations/postAccountExceptionsBulkClosed";
import usePostAccountExceptionsBulkReopen from "./src/mutations/postAccountExceptionsBulkReopen";
import { downloadAccountExceptionsExport } from "./src/mutations/exportAccountExceptions";
import getAccountShipmentsTracksBatch from "./src/getAccountShipmentsTracksBatch";
import getAccountCurrentPositionsV2 from "./src/getAccountCurrentPositionsV2";
import updateOrder from "./src/mutations/updateOrder";
import createAccountGroup from "./src/mutations/createAccountGroup";
import deleteAccountGroup from "./src/mutations/deleteAccountGroup";
import getDeviceCommands from "./src/getDeviceCommands";
import getDeviceMessages from "./src/getDeviceMessages";
import getDeviceEvents from "./src/getDeviceEvents";
import sendDeviceCommand from "./src/mutations/sendDeviceCommand";
import unassignDevice from "./src/mutations/unassignDevice";
import getAccountAnchors from "./src/getAccountAnchors";
import getAccountNearestGeofence from "./src/getAccountNearestGeofence";
import createAccountAnchor from "./src/mutations/createAccountAnchor";
import updateAccountAnchor from "./src/mutations/updateAccountAnchor";
import deleteAccountAnchor from "./src/mutations/deleteAccountAnchor";
import uploadAnchorImage from "./src/mutations/uploadAnchorImage";
import deleteAnchorImage from "./src/mutations/deleteAnchorImage";
import getAccountSubscriptions from "./src/getAccountSubscriptions";
import getSubscription from "./src/getSubscription";
import getAccountOrders from "./src/getAccountOrders";
import getOrder from "./src/getOrder";
import getAccountAddresses from "./src/getAccountAddresses";
import getAccountPaymentMethods from "./src/getAccountPaymentMethods";
import cancelSubscription from "./src/mutations/cancelSubscription";
import reactivateSubscription from "./src/mutations/reactivateSubscription";
import createAccountAddress from "./src/mutations/createAccountAddress";
import updateAccountAddress from "./src/mutations/updateAccountAddress";
import deleteAccountAddress from "./src/mutations/deleteAccountAddress";
import deleteAccountPaymentMethod from "./src/mutations/deleteAccountPaymentMethod";
import getAccountUsers from "./src/getAccountUsers";
import inviteAccountUser from "./src/mutations/inviteAccountUser";
import updateAccountUser from "./src/mutations/updateAccountUser";
import removeAccountUser from "./src/mutations/removeAccountUser";
import getAccountReports from "./src/getAccountReports";
import getAccountReport from "./src/getAccountReport";
import getPublicReport from "./src/getPublicReport";
import getDevicePositions from "./src/getDevicePositions";
import getDeviceStops from "./src/getDeviceStops";
import getDeviceEventsRange from "./src/getDeviceEventsRange";
import getAccountDwellGeofences from "./src/getAccountDwellGeofences";
import getAccountDwellGeofenceDevices from "./src/getAccountDwellGeofenceDevices";
import createAccountReport from "./src/mutations/createAccountReport";
import updateAccountReport from "./src/mutations/updateAccountReport";
import deleteAccountReport from "./src/mutations/deleteAccountReport";
import exportAccountReport from "./src/mutations/exportAccountReport";
import exportPublicReport from "./src/mutations/exportPublicReport";
import getAdminUsers from "./src/getAdminUsers";
import getAdminUserPermissions from "./src/getAdminUserPermissions";
import updateAdminUserPermissions from "./src/mutations/updateAdminUserPermissions";
import redactAccountData from "./src/mutations/redactAccountData";
import getCommitments from "./src/getCommitments";
import getAccountCommitments from "./src/getAccountCommitments";
import getAccountCommitment, { accountCommitmentQueryOptions } from "./src/getAccountCommitment";
import createAccountCommitment from "./src/mutations/createAccountCommitment";
import updateAccountCommitment from "./src/mutations/updateAccountCommitment";
import cancelAccountCommitment from "./src/mutations/cancelAccountCommitment";
import confirmAccountCommitment from "./src/mutations/confirmAccountCommitment";
import refundCommitmentDeposit from "./src/mutations/refundCommitmentDeposit";
import createCommitmentOrder from "./src/mutations/createCommitmentOrder";
import getAccountPublicApiTokens from "./src/getAccountPublicApiTokens";
import createAccountPublicApiToken from "./src/mutations/createAccountPublicApiToken";
import revokeAccountPublicApiToken from "./src/mutations/revokeAccountPublicApiToken";
import getAccountContainerTypes from "./src/getAccountContainerTypes";
import createAccountContainerType from "./src/mutations/createAccountContainerType";
import updateAccountContainerType from "./src/mutations/updateAccountContainerType";
import deleteAccountContainerType from "./src/mutations/deleteAccountContainerType";
import getAccountPlaceTypes from "./src/getAccountPlaceTypes";
import createAccountPlaceType from "./src/mutations/createAccountPlaceType";
import updateAccountPlaceType from "./src/mutations/updateAccountPlaceType";
import deleteAccountPlaceType from "./src/mutations/deleteAccountPlaceType";
import getAccountFindContainers from "./src/getAccountFindContainers";
import getAccountQuickbooks from "./src/getAccountQuickbooks";
import searchQuickbooksCustomers from "./src/searchQuickbooksCustomers";
import searchQuickbooksItems from "./src/searchQuickbooksItems";
import connectAccountQuickbooks from "./src/mutations/connectAccountQuickbooks";
import disconnectAccountQuickbooks from "./src/mutations/disconnectAccountQuickbooks";
import getQuickbooksConnection from "./src/getQuickbooksConnection";
import startQuickbooksAuthorization from "./src/mutations/startQuickbooksAuthorization";
import getAccountAttio from "./src/getAccountAttio";
import searchAttioCompanies from "./src/searchAttioCompanies";
import connectAccountAttio from "./src/mutations/connectAccountAttio";
import disconnectAccountAttio from "./src/mutations/disconnectAccountAttio";
import getAccountPayments from "./src/getAccountPayments";
import getProduct, { productQueryOptions } from "./src/getProduct";

const initializeQueries = (app, api, publicApi) => {
  app.use(VueQueryPlugin);
  setApi(api);
  if (publicApi) {
    setPublicApi(publicApi);
  }
};

export {
  getAccount,
  getPublicTracker,
  updateAccount,
  deleteBlacklistedMac,
  postBlacklistedMac,
  deleteFixedMac,
  postFixedMac,
  getFixedMac,
  getBlacklistedMac,
  postDialog,
  getAccountDialogs,
  postAccountDialog,
  updateAiDialog,
  deleteAiDialog,
  getAiDialogMessages,
  initializeQueries,
  getUserDevices,
  getAccountUser,
  getAccountDevices,
  getAccountDeviceList,
  ACCOUNT_DEVICE_LIST_PER_PAGE,
  fetchAccountDeviceListPage,
  toApiPagination,
  getAccountDevice,
  getAccountEvents,
  getAccountGeofences,
  getAccountGeofence,
  createAccountGeofence,
  updateAccountGeofence,
  deleteAccountGeofence,
  getAccountGroups,
  getAccountShipments,
  getAccountShipmentMetrics,
  getModels,
  getModel,
  createModel,
  updateModel,
  getCommandsByModel,
  postAiDialogMessage,
  postMessageFeedback,
  deleteMessageFeedback,
  getCommandBroadcasts,
  downloadCommandBroadcast,
  getAccountContext,
  postAccountContext,
  updateAccountContext,
  deleteAccountContext,
  getInsights,
  getInsight,
  getPayments,
  getPayment,
  getDevicePurchaseReviews,
  getDevicePurchaseReview,
  updateDevicePurchaseReviewSlots,
  findPayment,
  getAccountAiReports,
  getAccountPlaceSuggestions,
  getAccountPlaceSuggestion,
  declineAccountPlaceSuggestion,
  getAccountGeofenceRemovalSuggestions,
  getAccountGeofenceRemovalSuggestion,
  declineAccountGeofenceRemovalSuggestion,
  getAiReport,
  approveAiReport,
  generateAiReport,
  executeCommand,
  getAiCommand,
  revertCommand,
  updatePayment,
  updateAddress,
  getPaymentParts,
  createPaymentPart,
  sendPaymentToQuickBooks,
  deletePaymentPart,
  getPasskeyCredentials,
  passkeyBeginLogin,
  passkeyFinishLogin,
  passkeyBeginRegister,
  passkeyFinishRegister,
  passkeyDelete,
  getAccountMapOverlays,
  deleteMapOverlay,
  createMapOverlay,
  updateMapOverlay,
  updateDevice,
  createAccountShipment,
  postAccountShipmentRouteSegment,
  patchAccountShipmentRouteSegment,
  deleteAccountShipmentRouteSegment,
  usePostAccountShipmentDevices,
  usePostAccountShipmentPublish,
  usePostAccountShipmentStops,
  usePutAccountShipmentStops,
  getAccountShipmentRoutesBatch,
  getAccountLearnedRoutes,
  getAccountShipmentV2,
  ACCOUNT_SHIPMENT_V2_INCLUDE,
  usePatchAccountShipmentStop,
  usePatchAccountShipmentV2,
  usePostAccountShipmentFinished,
  usePostAccountShipmentsBulkArchive,
  usePostAccountShipmentsBulkDelete,
  getAccountShipmentEvents,
  getAccountExceptions,
  getAccountExceptionMetrics,
  getAccountShipmentExceptions,
  usePostAccountExceptionClosed,
  useDeleteAccountExceptionClosed,
  usePostAccountExceptionsBulkClosed,
  usePostAccountExceptionsBulkReopen,
  downloadAccountExceptionsExport,
  getAccountShipmentsTracksBatch,
  getAccountCurrentPositionsV2,
  updateOrder,
  createAccountGroup,
  deleteAccountGroup,
  getDeviceCommands,
  getDeviceMessages,
  getDeviceEvents,
  sendDeviceCommand,
  unassignDevice,
  getAccountAnchors,
  getAccountNearestGeofence,
  createAccountAnchor,
  updateAccountAnchor,
  deleteAccountAnchor,
  uploadAnchorImage,
  deleteAnchorImage,
  getAccountSubscriptions,
  getSubscription,
  getAccountOrders,
  getOrder,
  getAccountAddresses,
  getAccountPaymentMethods,
  cancelSubscription,
  reactivateSubscription,
  createAccountAddress,
  updateAccountAddress,
  deleteAccountAddress,
  deleteAccountPaymentMethod,
  getAccountUsers,
  inviteAccountUser,
  updateAccountUser,
  removeAccountUser,
  getAccountReports,
  getAccountReport,
  getPublicReport,
  getDevicePositions,
  getDeviceStops,
  getDeviceEventsRange,
  getAccountDwellGeofences,
  getAccountDwellGeofenceDevices,
  createAccountReport,
  updateAccountReport,
  deleteAccountReport,
  exportAccountReport,
  exportPublicReport,
  getAdminUsers,
  getAdminUserPermissions,
  updateAdminUserPermissions,
  redactAccountData,
  getCommitments,
  getAccountCommitments,
  getAccountCommitment,
  accountCommitmentQueryOptions,
  createAccountCommitment,
  updateAccountCommitment,
  cancelAccountCommitment,
  confirmAccountCommitment,
  refundCommitmentDeposit,
  createCommitmentOrder,
  getAccountPublicApiTokens,
  createAccountPublicApiToken,
  revokeAccountPublicApiToken,
  getAccountContainerTypes,
  createAccountContainerType,
  updateAccountContainerType,
  deleteAccountContainerType,
  getAccountPlaceTypes,
  createAccountPlaceType,
  updateAccountPlaceType,
  deleteAccountPlaceType,
  getAccountFindContainers,
  getAccountQuickbooks,
  searchQuickbooksCustomers,
  searchQuickbooksItems,
  connectAccountQuickbooks,
  disconnectAccountQuickbooks,
  getQuickbooksConnection,
  startQuickbooksAuthorization,
  getAccountAttio,
  searchAttioCompanies,
  connectAccountAttio,
  disconnectAccountAttio,
  getAccountPayments,
  getProduct,
  productQueryOptions,
  useQueryClient,
};
